import { MarketDataResponse } from '../types/marketTypes';

// Cache for market data to avoid duplicate API calls
let marketCache: MarketDataResponse | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

/**
 * Fetches market data for S&P 500, NASDAQ, Bitcoin, and Ethereum
 * Uses multiple free APIs with fallback mechanisms
 */
export const getMarketData = async (): Promise<MarketDataResponse> => {
    // Check cache first
    const now = Date.now();
    if (marketCache && (now - cacheTimestamp) < CACHE_DURATION) {
        console.log('📈 Using cached market data');
        return marketCache;
    }

    console.log('🔄 Fetching fresh market data...');

    try {
        // Use localhost:3001 in development, /api in production
        const apiUrl = import.meta.env.DEV
            ? 'http://localhost:3001/api/market-data'
            : '/api/market-data';

        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.message || 'Failed to fetch market data');
        }

        // Cache the result
        marketCache = data;
        cacheTimestamp = now;

        console.log('✅ Successfully fetched market data');
        return data;

    } catch (error) {
        console.error('❌ Error fetching market data:', error);

        // Return fallback data if API fails
        const fallbackData: MarketDataResponse = {
            success: true,
            data: {
                sp500: {
                    symbol: 'SPX',
                    name: 'S&P 500',
                    price: 4500.00,
                    change: 15.50,
                    changePercent: 0.35,
                    lastUpdated: new Date().toISOString(),
                },
                nasdaq: {
                    symbol: 'IXIC',
                    name: 'NASDAQ',
                    price: 14000.00,
                    change: 45.20,
                    changePercent: 0.32,
                    lastUpdated: new Date().toISOString(),
                },
                gold: {
                    symbol: 'XAU',
                    name: 'Gold',
                    price: 2650.00,
                    change: 15.50,
                    changePercent: 0.59,
                    lastUpdated: new Date().toISOString(),
                },
                bitcoin: {
                    symbol: 'BTC',
                    name: 'Bitcoin',
                    price: 45000.00,
                    change: 850.00,
                    changePercent: 1.92,
                    lastUpdated: new Date().toISOString(),
                },
                ethereum: {
                    symbol: 'ETH',
                    name: 'Ethereum',
                    price: 2800.00,
                    change: 45.00,
                    changePercent: 1.63,
                    lastUpdated: new Date().toISOString(),
                },
            },
            lastUpdated: new Date().toISOString(),
            source: 'Fallback Data',
        };

        // Cache fallback data
        marketCache = fallbackData;
        cacheTimestamp = now;

        return fallbackData;
    }
};

/**
 * Get formatted market data for display
 */
export const getFormattedMarketData = async () => {
    try {
        const marketData = await getMarketData();

        return {
            sp500: {
                ...marketData.data.sp500,
                formattedPrice: `$${marketData.data.sp500.price.toFixed(2)}`,
                formattedChange: `${marketData.data.sp500.change >= 0 ? '+' : ''}${marketData.data.sp500.change.toFixed(2)}`,
                formattedChangePercent: `${marketData.data.sp500.changePercent >= 0 ? '+' : ''}${marketData.data.sp500.changePercent.toFixed(2)}%`,
                isPositive: marketData.data.sp500.change >= 0,
            },
            nasdaq: {
                ...marketData.data.nasdaq,
                formattedPrice: `$${marketData.data.nasdaq.price.toFixed(2)}`,
                formattedChange: `${marketData.data.nasdaq.change >= 0 ? '+' : ''}${marketData.data.nasdaq.change.toFixed(2)}`,
                formattedChangePercent: `${marketData.data.nasdaq.changePercent >= 0 ? '+' : ''}${marketData.data.nasdaq.changePercent.toFixed(2)}%`,
                isPositive: marketData.data.nasdaq.change >= 0,
            },
            gold: {
                ...marketData.data.gold,
                formattedPrice: `$${marketData.data.gold.price.toFixed(2)}`,
                formattedChange: `${marketData.data.gold.change >= 0 ? '+' : ''}${marketData.data.gold.change.toFixed(2)}`,
                formattedChangePercent: `${marketData.data.gold.changePercent >= 0 ? '+' : ''}${marketData.data.gold.changePercent.toFixed(2)}%`,
                isPositive: marketData.data.gold.change >= 0,
            },
            bitcoin: {
                ...marketData.data.bitcoin,
                formattedPrice: `$${marketData.data.bitcoin.price.toLocaleString()}`,
                formattedChange: `${marketData.data.bitcoin.change >= 0 ? '+' : ''}$${marketData.data.bitcoin.change.toLocaleString()}`,
                formattedChangePercent: `${marketData.data.bitcoin.changePercent >= 0 ? '+' : ''}${marketData.data.bitcoin.changePercent.toFixed(2)}%`,
                isPositive: marketData.data.bitcoin.change >= 0,
            },
            ethereum: {
                ...marketData.data.ethereum,
                formattedPrice: `$${marketData.data.ethereum.price.toLocaleString()}`,
                formattedChange: `${marketData.data.ethereum.change >= 0 ? '+' : ''}$${marketData.data.ethereum.change.toLocaleString()}`,
                formattedChangePercent: `${marketData.data.ethereum.changePercent >= 0 ? '+' : ''}${marketData.data.ethereum.changePercent.toFixed(2)}%`,
                isPositive: marketData.data.ethereum.change >= 0,
            },
        };
    } catch (error) {
        console.error('Error formatting market data:', error);
        return null;
    }
};
